/**
 * Client for the File Manager API (`/files`). Folders form a tenant tree;
 * files are the existing attachments tagged with an organizational `folderId`.
 * Downloads/deletes reuse the attachments client (authed blob fetch).
 */
import type { FileItem, FilesList, FileStats, Folder, FolderCreate, FolderUpdate } from '@erp/shared';

import { api } from './api.js';

export {
  fetchAttachmentObjectUrl,
  deleteAttachment,
  isImageMime,
  ATTACHMENT_ALLOWED_MIME,
  ATTACHMENT_MAX_BYTES,
} from './attachments.js';
export type { FileItem, FileStats, Folder } from '@erp/shared';

export async function listFolders(): Promise<Folder[]> {
  const r = await api().get<{ items: Folder[] }>('/files/folders');
  return r.data.items;
}

export async function createFolder(input: FolderCreate): Promise<Folder> {
  const r = await api().post<Folder>('/files/folders', input);
  return r.data;
}

export async function updateFolder(id: string, input: FolderUpdate): Promise<Folder> {
  const r = await api().patch<Folder>(`/files/folders/${id}`, input);
  return r.data;
}

export async function deleteFolder(id: string): Promise<void> {
  await api().delete(`/files/folders/${id}`);
}

export interface ListFilesParams {
  folderId?: string;
  q?: string;
  entityType?: string;
  /** A smart view spans several sources — sent comma-separated. */
  entityTypes?: readonly string[];
  limit?: number;
  offset?: number;
}

export async function listFiles(params: ListFilesParams = {}): Promise<FilesList> {
  const { entityTypes, ...rest } = params;
  const query: Record<string, string | number> = { ...rest } as Record<string, string | number>;
  if (entityTypes && entityTypes.length > 0) query.entityTypes = entityTypes.join(',');
  const r = await api().get<FilesList>('/files', { params: query });
  return r.data;
}

/** Tenant-wide counters for the sidebar + storage meter (no file rows shipped). */
export async function getFileStats(): Promise<FileStats> {
  const r = await api().get<FileStats>('/files/stats');
  return r.data;
}

export async function moveFile(id: string, folderId: string | null): Promise<FileItem> {
  const r = await api().patch<FileItem>(`/files/${id}`, { folderId });
  return r.data;
}

/** Upload a standalone file into the manager (optionally into a folder). */
export async function uploadToFolder(file: File, folderId: string | null): Promise<{ id: string }> {
  const body = await file.arrayBuffer();
  const params: Record<string, string> = { filename: file.name, mimeType: file.type };
  if (folderId) params.folderId = folderId;
  const r = await api().post<{ id: string }>('/files', body, {
    params,
    headers: { 'Content-Type': 'application/octet-stream' },
    transformRequest: [(d) => d],
  });
  return r.data;
}
